import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const ResumeAnalyzerPreview = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Optimize Your Resume with AI
            </h2>
            <p className="text-lg mb-8 text-indigo-100">
              Get instant feedback on your resume, match with top jobs, and
              improve your chances of landing your dream role.
            </p>
            <ul className="space-y-4 mb-8">
              {[
                "AI-powered resume analysis",
                "Keyword optimization for ATS",
                "Industry-specific suggestions",
                "Instant job matches from top companies",
                "Personalized improvement tips",
              ].map((feature, index) => (
                <li key={index} className="flex items-center space-x-3">
                  <svg
                    className="h-5 w-5 text-indigo-200"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/resume-analyzer")}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg transform transition hover:-translate-y-0.5"
            >
              Analyze Your Resume
              <svg
                className="ml-2 -mr-1 w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Right Column - Interactive Preview */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg shadow-xl p-6"
            >
              <div className="space-y-6">
                {/* Resume Analysis Preview */}
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    Upload your resume to get started
                  </p>
                </div>

                {/* Analysis Features Preview */}
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-md p-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Keywords Analysis
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        "Java",
                        "Node.js",
                        "Node.js",
                        "Python",
                        "React.js",
                        "Fullstack",
                        "backend",
                        "frontend",
                        ,
                        "C++",
                        ,
                        "SQL",
                        "NoSQL",
                        "MongoDB",
                        "MySQL",
                      ].map((skill) => (
                        <span
                          key={skill}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-md p-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Job Matches
                    </h4>
                    <div className="mt-2 space-y-2">
                      {[
                        "Software Engineer",
                        "Full Stack Developer",
                        "Frontend Developer",
                      ].map((job) => (
                        <div
                          key={job}
                          className="flex items-center justify-between text-sm text-gray-600"
                        >
                          <span>{job}</span>
                          <span className="text-indigo-600">90% match</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
            <div className="absolute -bottom-8 -left-4 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResumeAnalyzerPreview;
